// frontend/src/services/auth.service.ts
//
// Supabase Auth ile doğrudan konuşan servis. Email/şifre, Google ve GitHub
// OAuth akışlarını sarmalar. `role` ve `full_name` gibi bilgiler signUp
// sırasında user_metadata'ya yazılır; backend'deki `handle_new_user`
// trigger'ı bunu okuyup `profiles` tablosuna otomatik satır ekler
// (bkz. database/migrations/0003_auth_triggers.sql).

import { supabase } from '../lib/supabaseClient';
import { SignUpStudentParams, SignUpParentParams } from '../types/auth.types';

export interface ParsedAuthErrorInfo {
  message: string;
  isServiceUnavailable: boolean;
  isRateLimit: boolean;
  isSmtpError: boolean;
  status?: number;
}

/**
 * Supabase Auth ve HTTP hatalarını kullanıcı dostu, açıklayıcı mesajlara dönüştürür.
 * 500/503 Service Unavailable, Rate Limit (429) ve SMTP kesintilerini özel olarak yakalar.
 */
export function parseAuthError(error: any): ParsedAuthErrorInfo {
  if (!error) {
    return {
      message: 'Bilinmeyen bir hata oluştu.',
      isServiceUnavailable: false,
      isRateLimit: false,
      isSmtpError: false,
    };
  }

  const rawMsg = (error.message || error.error_description || String(error)).toLowerCase();
  const status = Number(error.status || error.statusCode || 0);

  // 1. SMTP / E-posta Gönderim Hatası
  if (
    rawMsg.includes('error sending confirmation mail') ||
    rawMsg.includes('error sending email') ||
    rawMsg.includes('smtp') ||
    (rawMsg.includes('mail') && rawMsg.includes('error')) ||
    rawMsg.includes('over email rate limit') ||
    rawMsg.includes('over_email_send_rate_limit')
  ) {
    return {
      message: 'Hesabınız oluşturuldu ancak e-posta doğrulama servisinde geçici bir yoğunluk var. Lütfen birkaç dakika sonra e-postanızı (Spam klasörü dahil) kontrol edin veya giriş yapmayı deneyin.',
      isServiceUnavailable: false,
      isRateLimit: rawMsg.includes('rate limit'),
      isSmtpError: true,
      status,
    };
  }

  // 2. 500 / 502 / 503 / 504 / Service Unavailable / Gateway Timeout
  if (
    status >= 500 ||
    rawMsg.includes('service unavailable') ||
    rawMsg.includes('gateway timeout') ||
    rawMsg.includes('bad gateway') ||
    rawMsg.includes('internal server error')
  ) {
    return {
      message: 'Kimlik doğrulama sunucuları şu anda yoğun veya geçici olarak bakımda. Lütfen birkaç dakika sonra tekrar deneyin.',
      isServiceUnavailable: true,
      isRateLimit: false,
      isSmtpError: false,
      status,
    };
  }

  // 3. 429 / Rate Limit
  if (status === 429 || rawMsg.includes('rate limit') || rawMsg.includes('too many requests')) {
    return {
      message: 'Çok fazla deneme yapıldı. Güvenliğiniz için lütfen birkaç dakika bekledikten sonra tekrar deneyin.',
      isServiceUnavailable: false,
      isRateLimit: true,
      isSmtpError: false,
      status: 429,
    };
  }

  // 4. Askıya Alınmış / Banlı Hesap
  if (rawMsg.includes('user is banned') || rawMsg.includes('account_banned')) {
    return {
      message: 'Bu hesap askıya alınmıştır. Detaylı bilgi için destek ekibimizle iletişime geçebilirsiniz.',
      isServiceUnavailable: false,
      isRateLimit: false,
      isSmtpError: false,
      status,
    };
  }

  // 5. Zaten Kayıtlı Kullanıcı
  if (rawMsg.includes('user already registered') || rawMsg.includes('already exists')) {
    return {
      message: 'Bu e-posta adresiyle kayıtlı bir hesap zaten var. Lütfen giriş yapmayı deneyin veya şifrenizi sıfırlayın.',
      isServiceUnavailable: false,
      isRateLimit: false,
      isSmtpError: false,
      status,
    };
  }

  // 6. Yanlış Kimlik Bilgileri
  if (rawMsg.includes('invalid login credentials') || rawMsg.includes('invalid credentials')) {
    return {
      message: 'E-posta adresi veya şifre hatalı. Lütfen bilgilerinizi kontrol edin.',
      isServiceUnavailable: false,
      isRateLimit: false,
      isSmtpError: false,
      status,
    };
  }

  // 7. Doğrulanmamış E-posta
  if (rawMsg.includes('email not confirmed')) {
    return {
      message: 'E-posta adresiniz henüz doğrulanmamış. Lütfen gelen kutunuzdaki aktivasyon linkine tıklayın.',
      isServiceUnavailable: false,
      isRateLimit: false,
      isSmtpError: false,
      status,
    };
  }

  // 8. Ağ Bağlantısı Hatası
  if (rawMsg.includes('failed to fetch') || rawMsg.includes('network error') || rawMsg.includes('timeout')) {
    return {
      message: 'Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.',
      isServiceUnavailable: true,
      isRateLimit: false,
      isSmtpError: false,
      status,
    };
  }

  return {
    message: error.message || 'İşlem gerçekleştirilemedi. Lütfen tekrar deneyin.',
    isServiceUnavailable: false,
    isRateLimit: false,
    isSmtpError: false,
    status,
  };
}

export class FriendlyAuthError extends Error {
  status?: number;
  code?: string;
  isServiceUnavailable: boolean;
  isRateLimit: boolean;
  isSmtpError: boolean;

  constructor(rawError: any) {
    const info = parseAuthError(rawError);
    super(info.message);
    this.name = 'FriendlyAuthError';
    this.status = info.status;
    this.code = rawError?.code;
    this.isServiceUnavailable = info.isServiceUnavailable;
    this.isRateLimit = info.isRateLimit;
    this.isSmtpError = info.isSmtpError;
  }
}

export const authService = {
  async signUpStudent({ email, password, fullName, phone, age, schoolName, city, interests }: SignUpStudentParams) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { role: 'student', full_name: fullName, phone, age: String(age), school_name: schoolName, city, interests },
        },
      });
      if (error) throw new FriendlyAuthError(error);
      return data;
    } catch (err: any) {
      if (err instanceof FriendlyAuthError) throw err;
      throw new FriendlyAuthError(err);
    }
  },

  async signUpParent({ email, password, fullName, phone }: SignUpParentParams) {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { role: 'parent', full_name: fullName, phone },
        },
      });
      if (error) throw new FriendlyAuthError(error);
      return data;
    } catch (err: any) {
      if (err instanceof FriendlyAuthError) throw err;
      throw new FriendlyAuthError(err);
    }
  },

  async signInWithPassword(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw new FriendlyAuthError(error);
      return data;
    } catch (err: any) {
      if (err instanceof FriendlyAuthError) throw err;
      throw new FriendlyAuthError(err);
    }
  },

  async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw new FriendlyAuthError(error);
      return data;
    } catch (err: any) {
      if (err instanceof FriendlyAuthError) throw err;
      throw new FriendlyAuthError(err);
    }
  },

  async signInWithGitHub() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw new FriendlyAuthError(error);
      return data;
    } catch (err: any) {
      if (err instanceof FriendlyAuthError) throw err;
      throw new FriendlyAuthError(err);
    }
  },

  /**
   * Şifre sıfırlama e-postası gönderir.
   */
  async requestPasswordReset(email: string) {
    try {
      const redirectUrl = `${window.location.origin}/auth/callback`;
      const { data, error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: redirectUrl,
      });
      if (error) throw new FriendlyAuthError(error);
      return data;
    } catch (err: any) {
      if (err instanceof FriendlyAuthError) throw err;
      throw new FriendlyAuthError(err);
    }
  },

  /**
   * Sıfırlama bağlantısıyla girildikten sonra yeni şifreyi günceller.
   */
  async updatePassword(newPassword: string) {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw new FriendlyAuthError(error);
      return data;
    } catch (err: any) {
      if (err instanceof FriendlyAuthError) throw err;
      throw new FriendlyAuthError(err);
    }
  },

  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw new FriendlyAuthError(error);
    } catch (err: any) {
      if (err instanceof FriendlyAuthError) throw err;
      throw new FriendlyAuthError(err);
    }
  },

  async getSession() {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw new FriendlyAuthError(error);
      return data.session;
    } catch (err: any) {
      if (err instanceof FriendlyAuthError) throw err;
      throw new FriendlyAuthError(err);
    }
  },

  /**
   * Aktif oturumun access token'ını döndürür — backend'e atılan isteklerde
   * Authorization: Bearer <token> header'ı için kullanılır.
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const session = await this.getSession();
      return session?.access_token ?? null;
    } catch {
      return null;
    }
  },
};
